import { useState, useMemo, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Maximize2, X, Loader2, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartInfoButton } from "@/components/ChartInfoButton";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, ComposedChart, Bar, BarChart, ReferenceLine } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import {
  CORE_PALETTE,
  getChartColors,
  AXIS_TICK_STYLE,
  CHART_MARGIN,
  CHART_MARGIN_DUAL_Y,
  getXAxisInterval,
  formatYAxisLabel,
  renderMonthYearTick,
  MONTH_YEAR_AXIS_HEIGHT,
} from "@/analytics/chart.utilities";
import type { ChartConfig } from "@/analytics/chart.config";

const LEGEND_LABELS: Record<string, string> = {
  chargeableHours: "Chargeable Hours",
  nonChargeableHours: "Non‑Chargeable Hours",
  utilisationPct: "Utilisation %",
  utilisation: "Utilisation %",
  ash: "Absence / Holiday / Sickness",
  lAndD: "Learning & Development",
  other: "Other Non‑Billable",
};


function formatAxisMonth(label: string): string {
  const m = label.match(/^(\d{4})-(\d{2})/);
  if (m) {
    const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${names[parseInt(m[2]) - 1]} ${m[1]}`;
  }
  return label;
}

interface ChartCarouselProps {
  chartData: any;
  visibleCharts: ChartConfig[];
  loadingKeys?: Set<string>;
  chartErrors?: Record<string, boolean>;
  onRetry?: (dataKey: string) => void;
}

const PREVIEW_SCALE = 0.35;
const PREVIEW_CHART_W = 500;
const PREVIEW_CHART_H = 250;
const PREVIEW_W = Math.round(PREVIEW_CHART_W * PREVIEW_SCALE);
const PREVIEW_H = Math.round(PREVIEW_CHART_H * PREVIEW_SCALE);

const ChartCarousel = ({ chartData, visibleCharts, loadingKeys, chartErrors, onRetry }: ChartCarouselProps) => {
  // Track fullscreen by index for prev/next navigation
  const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);
  const colorPalette = getChartColors().primary;


  const firstCardRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState(0);

  useEffect(() => {
    const el = firstCardRef.current;
    if (!el) return;
    const obs = new ResizeObserver(entries => setCardWidth(entries[0].contentRect.width));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (fullscreenIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") setFullscreenIndex(i => (i !== null && i > 0 ? i - 1 : i));
      if (e.key === "ArrowRight") setFullscreenIndex(i => (i !== null ? i + 1 : i)); // clamped below
      if (e.key === "Escape") setFullscreenIndex(null);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [fullscreenIndex]);


  type ChartFactory = (heightClass: string) => React.ReactNode;

  const chartFactories: Record<string, ChartFactory | false> = useMemo(() => ({
    monthlyUtilisationCombo: !!chartData?.monthlyUtilisationCombo && ((heightClass: string) => {
      const labels: string[] = chartData.monthlyUtilisationCombo.labels;
      const xInt = getXAxisInterval(cardWidth, CHART_MARGIN_DUAL_Y, labels.length, true);
      return (
        <ChartContainer config={{}} className={`${heightClass} w-full`} style={{ aspectRatio: 'auto', display: 'block' }}>
          <ComposedChart
            data={labels.map((label: string, idx: number) => ({
              month: label,
              chargeableHours: chartData.monthlyUtilisationCombo.chargeableHours[idx],
              utilisationPct: chartData.monthlyUtilisationCombo.utilisationPct[idx],
              plannedChargeableHours: chartData.monthlyUtilisationCombo.plannedChargeableHours?.[idx],
              forecastUtilisationPct: chartData.monthlyUtilisationCombo.forecastUtilisationPct?.[idx],
            }))}
            margin={CHART_MARGIN_DUAL_Y}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" interval={xInt} tickFormatter={formatAxisMonth} tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
            <YAxis width={45} yAxisId="left" tickFormatter={v => formatYAxisLabel(v, 'hours')} tick={AXIS_TICK_STYLE} />
            <YAxis width={45} yAxisId="right" orientation="right" domain={[0, 100]} tickFormatter={v => formatYAxisLabel(v, 'percent')} tick={AXIS_TICK_STYLE} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Legend />
            <Bar yAxisId="left" dataKey="chargeableHours" name={LEGEND_LABELS.chargeableHours} fill={CORE_PALETTE.forestGreen} />
            <Bar yAxisId="left" dataKey="plannedChargeableHours" name="Planned Chargeable Hours" fill={CORE_PALETTE.teal} />
            <Line yAxisId="right" type="monotone" dataKey="utilisationPct" name={LEGEND_LABELS.utilisationPct} stroke={CORE_PALETTE.actionOrange} strokeWidth={2} />
            <Line yAxisId="right" type="monotone" dataKey="forecastUtilisationPct" name="Forecast Utilisation %" stroke={CORE_PALETTE.actionOrange} strokeWidth={2} strokeDasharray="5 4" dot={false} />
          </ComposedChart>
        </ChartContainer>
      );
    }),

    averageCor: !!chartData?.averageCor && ((heightClass: string) => {
      const labels: string[] = chartData.averageCor.labels;
      const margin = { ...CHART_MARGIN, right: 30 };
      const xInt = getXAxisInterval(cardWidth, margin, labels.length);
      return (
        <ChartContainer config={{}} className={`${heightClass} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={labels.map((label: string, idx: number) => ({
                month: label,
                ...chartData.averageCor.datasets.reduce((acc: any, ds: any) => ({
                  ...acc, [ds.label]: ds.data[idx],
                }), {})
              }))}
              margin={margin}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" interval={xInt} tickFormatter={formatAxisMonth} tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={45} tickFormatter={v => formatYAxisLabel(v, 'rate')} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Legend />
              {chartData.averageCor.datasets.map((ds: any, idx: number) => (
                <Line key={ds.label ?? `series-${idx}`} type="monotone" dataKey={ds.label} name={ds.label} stroke={colorPalette[idx % colorPalette.length].border} strokeWidth={2} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      );
    }),

    utilisationByLocation: !!chartData?.utilisationByLocation && ((heightClass: string) => {
      const labels: string[] = chartData.utilisationByLocation.labels;
      const margin = { ...CHART_MARGIN, right: 30 };
      const xInt = getXAxisInterval(cardWidth, margin, labels.length);
      return (
        <ChartContainer config={{}} className={`${heightClass} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={labels.map((label: string, idx: number) => ({
                month: label,
                ...chartData.utilisationByLocation.datasets.reduce((acc: any, ds: any) => ({
                  ...acc, [ds.label]: ds.data[idx],
                }), {})
              }))}
              margin={margin}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" interval={xInt} tickFormatter={formatAxisMonth} tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={45} domain={[0, 100]} tickFormatter={v => formatYAxisLabel(v, 'percent')} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Legend />
              {chartData.utilisationByLocation.datasets.map((ds: any, idx: number) => (
                <Line key={ds.label ?? `series-${idx}`} type="monotone" dataKey={ds.label} name={ds.label} stroke={colorPalette[idx % colorPalette.length].border} strokeWidth={2} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      );
    }),

    nonChargeableHoursByActivity: !!chartData?.nonChargeableHoursByActivity && ((heightClass: string) => {
      const labels: string[] = chartData.nonChargeableHoursByActivity.labels;
      const margin = { ...CHART_MARGIN, right: 30 };
      const xInt = getXAxisInterval(cardWidth, margin, labels.length);
      return (
        <ChartContainer config={{}} className={`${heightClass} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={labels.map((label: string, idx: number) => ({
                month: label,
                ...chartData.nonChargeableHoursByActivity.datasets.reduce((acc: any, ds: any) => ({
                  ...acc, [ds.label]: ds.data[idx],
                }), {})
              }))}
              margin={margin}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" interval={xInt} tickFormatter={formatAxisMonth} tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={45} tickFormatter={v => formatYAxisLabel(v, 'gbp')} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Legend />
              {chartData.nonChargeableHoursByActivity.datasets.map((ds: any, idx: number) => (
                <Line key={ds.label ?? `series-${idx}`} type="monotone" dataKey={ds.label} name={ds.label} stroke={colorPalette[idx % colorPalette.length].border} strokeWidth={2} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      );
    }),

    nonChargeableHoursByActivityBreakdown: !!chartData?.nonChargeableHoursByActivityBreakdown && ((heightClass: string) => {
      const labels: string[] = chartData.nonChargeableHoursByActivityBreakdown.labels;
      const margin = { ...CHART_MARGIN, right: 30 };
      const xInt = getXAxisInterval(cardWidth, margin, labels.length);
      return (
        <ChartContainer config={{}} className={`${heightClass} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={labels.map((label: string, idx: number) => ({
                month: label,
                ...chartData.nonChargeableHoursByActivityBreakdown.datasets.reduce((acc: any, ds: any) => ({
                  ...acc, [ds.label]: ds.data[idx],
                }), {})
              }))}
              margin={margin}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" interval={xInt} tickFormatter={formatAxisMonth} tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={45} tickFormatter={v => formatYAxisLabel(v, 'hours')} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Legend />
              {chartData.nonChargeableHoursByActivityBreakdown.datasets.map((ds: any, idx: number) => (
                <Line key={ds.label ?? `series-${idx}`} type="monotone" dataKey={ds.label} name={ds.label} stroke={colorPalette[idx % colorPalette.length].border} strokeWidth={2} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      );
    }),

    nonChargeableGbpByService: !!chartData?.nonChargeableGbpByService && ((heightClass: string) => {
      const labels: string[] = chartData.nonChargeableGbpByService.labels;
      const margin = { ...CHART_MARGIN, right: 30 };
      const xInt = getXAxisInterval(cardWidth, margin, labels.length);
      return (
        <ChartContainer config={{}} className={`${heightClass} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={labels.map((label: string, idx: number) => ({
                month: label,
                ...chartData.nonChargeableGbpByService.datasets.reduce((acc: any, ds: any) => ({
                  ...acc, [ds.label]: ds.data[idx],
                }), {})
              }))}
              margin={margin}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" interval={xInt} tickFormatter={formatAxisMonth} tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={45} tickFormatter={v => formatYAxisLabel(v, 'gbp')} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Legend />
              {chartData.nonChargeableGbpByService.datasets.map((ds: any, idx: number) => (
                <Line key={ds.label ?? `series-${idx}`} type="monotone" dataKey={ds.label} name={ds.label} stroke={colorPalette[idx % colorPalette.length].border} strokeWidth={2} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      );
    }),

    utilisationByGrade: !!chartData?.utilisationByGrade && ((heightClass: string) => {
      const labels: string[] = chartData.utilisationByGrade.labels;
      const averageUtilPct: number | null = chartData.utilisationByGrade.averageUtilPct;
      return (
        <ChartContainer config={{}} className={`${heightClass} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={labels.map((label: string, idx: number) => ({
                category: label,
                ...chartData.utilisationByGrade.datasets.reduce((acc: any, ds: any) => ({
                  ...acc, [ds.label]: ds.data[idx],
                }), {})
              }))}
              margin={CHART_MARGIN}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="category" interval={0} angle={-20} textAnchor="end" height={60} tick={AXIS_TICK_STYLE} />
              <YAxis width={45} domain={[0, 100]} tickFormatter={v => formatYAxisLabel(v, 'percent')} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Legend />
              {averageUtilPct !== null && (
                <ReferenceLine y={averageUtilPct} stroke={CORE_PALETTE.actionOrange} strokeDasharray="4 4" label={{ value: `Avg ${averageUtilPct}%`, position: 'insideTopRight', fontSize: 11 }} />
              )}
              {chartData.utilisationByGrade.datasets.map((ds: any, idx: number) => (
                <Bar key={ds.label ?? `series-${idx}`} dataKey={ds.label} name={ds.label} fill={colorPalette[idx % colorPalette.length].border} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      );
    }),

    burnoutRisk: !!chartData?.burnoutRisk && ((heightClass: string) => {
      const labels: string[] = chartData.burnoutRisk.labels;
      const margin = { ...CHART_MARGIN, right: 30 };
      const xInt = getXAxisInterval(cardWidth, margin, labels.length);
      return (
        <ChartContainer config={{}} className={`${heightClass} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={labels.map((label: string, idx: number) => ({
                month: label,
                ...chartData.burnoutRisk.datasets.reduce((acc: any, ds: any) => ({
                  ...acc, [ds.label]: ds.data[idx],
                }), {})
              }))}
              margin={margin}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" interval={xInt} tickFormatter={formatAxisMonth} tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={45} tickFormatter={v => formatYAxisLabel(v, 'percent')} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Legend />
              {chartData.burnoutRisk.datasets.map((ds: any, idx: number) => (
                <Line key={ds.label ?? `series-${idx}`} type="monotone" dataKey={ds.label} name={ds.label} stroke={colorPalette[idx % colorPalette.length].border} strokeWidth={2} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      );
    }),

  // cardWidth drives recalculation when the chart container resizes
  }), [chartData, colorPalette, cardWidth]); // eslint-disable-line react-hooks/exhaustive-deps

  const charts = useMemo(() =>
    visibleCharts
      .filter(config => !!chartFactories[config.dataKey])
      .map(config => ({
        id: config.id,
        dataKey: config.dataKey,
        title: config.title,
        description: config.description,
        renderChart: chartFactories[config.dataKey] as ChartFactory,
      })),
    [visibleCharts, chartFactories]
  );

  // Clamp fullscreen index to valid range
  const clampedIndex = fullscreenIndex !== null
    ? Math.max(0, Math.min(fullscreenIndex, charts.length - 1))
    : null;
  const fullscreenChart = clampedIndex !== null ? charts[clampedIndex] ?? null : null;

  const canGoPrev = clampedIndex !== null && clampedIndex > 0;
  const canGoNext = clampedIndex !== null && clampedIndex < charts.length - 1;

  const nextChart = canGoNext && clampedIndex !== null ? charts[clampedIndex + 1] : null;

  if (!charts.length) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-96">
          <p className="text-muted-foreground">No charts available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-2">
        {charts.map((chart, idx) => {
          const isOddLast = idx === charts.length - 1 && charts.length % 2 !== 0;
          const isLoading = loadingKeys?.has(chart.dataKey) ?? false;
          const hasError = chartErrors?.[chart.dataKey] ?? false;

          return (
            <div
              key={chart.id}
              ref={idx === 0 ? firstCardRef : undefined}
              className={isOddLast ? "lg:col-start-2 lg:col-span-2" : "lg:col-span-2"}
            >
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0" style={{ padding: '5px' }}>
                  <div className="flex items-center gap-1 min-w-0">
                    <CardTitle className="!text-sm truncate">{chart.title}</CardTitle>
                    <ChartInfoButton title={chart.title} description={chart.description} />
                  </div>
                </CardHeader>
                <CardContent style={{ padding: '5px' }}>
                  
                  <div className="relative w-full">
                    {chart.renderChart("h-[250px]")}
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setFullscreenIndex(idx)}
                      aria-label="Fullscreen"

                      className="absolute bottom-1 left-1 h-6 w-6 bg-background/70 hover:bg-primary/10 transition-all duration-200 hover:scale-110 shadow-sm lg:left-auto lg:right-1"
                    >
                      <Maximize2 className="h-3.5 w-3.5" />
                    </Button>
                    {hasError && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/75 rounded">
                        <p className="text-sm text-muted-foreground">Failed to load</p>
                        <Button size="sm" variant="outline" onClick={() => onRetry?.(chart.dataKey)}>
                          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                          Retry
                        </Button>
                      </div>
                    )}
                    {isLoading && !hasError && (
                      <div className="absolute top-2 right-2 pointer-events-none">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>

      {/* Per-chart fullscreen overlay */}
      {fullscreenChart && clampedIndex !== null && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-md"
            onClick={() => setFullscreenIndex(null)}
          />

          {/* Fullscreen panel */}
          <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-gradient-to-b from-background via-background to-muted/30">

            {/* Header */}
            <div className="pt-safe shrink-0 flex items-center justify-between gap-2 px-3 pt-4 pb-3 sm:px-10 sm:pt-6 sm:pb-4 lg:px-20">
              <div className="flex items-center gap-2 min-w-0 sm:gap-3">
                <h2 className="truncate text-base font-semibold tracking-tight sm:text-2xl">{fullscreenChart.title}</h2>
                <ChartInfoButton title={fullscreenChart.title} description={fullscreenChart.description} />
                {/* Counter pill */}
                <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground shrink-0">
                  {clampedIndex + 1} / {charts.length}
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setFullscreenIndex(null)}
                aria-label="Exit fullscreen"
                className="h-9 w-9 rounded-full hover:bg-destructive/10 hover:text-destructive transition-all duration-200 shrink-0"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Thin gradient separator */}
            <div className="shrink-0 h-px mx-6 bg-gradient-to-r from-transparent via-border to-transparent" />

            {/* Chart area — card + side nav arrows */}
            <div className="flex-1 min-h-0 relative flex items-center px-2 py-3 sm:px-12 sm:py-4 lg:px-20">

              {/* Left nav arrow */}
              <button
                disabled={!canGoPrev}
                onClick={() => setFullscreenIndex(i => (i !== null ? i - 1 : i))}
                aria-label="Previous chart"
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 sm:left-3
                  h-9 w-9 sm:h-12 sm:w-12 rounded-full flex items-center justify-center
                  bg-background/80 backdrop-blur-sm border border-border/60
                  shadow-lg hover:bg-primary/10 hover:border-primary/40 hover:scale-110
                  transition-all duration-200 disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:bg-background/80"
              >
                <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>

              {/* Chart card */}
              <div className="w-full h-full rounded-2xl border border-border/60 bg-card shadow-2xl overflow-hidden flex flex-col">
                {/* Card title bar */}
                <div className="shrink-0 flex items-center gap-2 px-5 pt-4 pb-2 border-b border-border/40">
                  <div className="h-2 w-2 rounded-full bg-primary/60" />
                  <span className="text-sm font-medium text-muted-foreground">{fullscreenChart.title}</span>
                </div>
                {/* Chart body — h-full fills the flex-1 space so no dead whitespace */}
                <div className="flex-1 min-h-0 p-4">
                  {fullscreenChart.renderChart("h-full")}
                </div>
              </div>

              {/* Right nav arrow */}
              <button
                disabled={!canGoNext}
                onClick={() => setFullscreenIndex(i => (i !== null ? i + 1 : i))}
                aria-label="Next chart"
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 sm:right-3
                  h-9 w-9 sm:h-12 sm:w-12 rounded-full flex items-center justify-center
                  bg-background/80 backdrop-blur-sm border border-border/60
                  shadow-lg hover:bg-primary/10 hover:border-primary/40 hover:scale-110
                  transition-all duration-200 disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:bg-background/80"
              >
                <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>

            {/* Bottom strip — dots + next preview */}
            <div className="pb-safe shrink-0 px-3 pb-4 pt-2 sm:px-6 sm:pb-5 sm:pt-3">
              {/* Thin gradient separator */}
              <div className="h-px mb-4 bg-gradient-to-r from-transparent via-border to-transparent" />

              <div className="flex items-center justify-center gap-4 sm:gap-10">

                {/* Dot indicators */}
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {charts.map((c, i) => (
                    <button
                      key={i}
                      onClick={() => setFullscreenIndex(i)}
                      aria-label={`Go to ${c.title}`}
                      className={`rounded-full transition-all duration-300 ${
                        i === clampedIndex
                          ? "w-5 h-2 bg-primary"
                          : "w-2 h-2 bg-muted-foreground/25 hover:bg-muted-foreground/50"
                      }`}
                    />
                  ))}
                </div>

                {/* Next chart preview */}
                {nextChart && (
                  <button
                    className="group hidden flex-col items-center gap-1.5 rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm px-3 pt-2 pb-2.5 shadow-sm
                      hover:border-primary/40 hover:bg-card hover:shadow-md transition-all duration-200 sm:flex"
                    onClick={() => setFullscreenIndex(i => (i !== null ? i + 1 : i))}
                    aria-label={`Go to ${nextChart.title}`}
                  >
                    {/* "Up next" label */}
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium self-start flex items-center gap-1">
                      Up next <ChevronRight className="h-3 w-3" />
                    </span>
                    {/* Thumbnail with title overlay */}
                    <div
                      className="rounded-lg border border-border/40 bg-background overflow-hidden shrink-0"
                      style={{ width: PREVIEW_W, height: PREVIEW_H, position: "relative" }}
                    >
                      {/* Scaled chart */}
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: PREVIEW_CHART_W,
                          height: PREVIEW_CHART_H,
                          transform: `scale(${PREVIEW_SCALE})`,
                          transformOrigin: "top left",
                          pointerEvents: "none",
                        }}
                      >
                        {nextChart.renderChart("h-[250px]")}
                      </div>
                    </div>
                    {/* Full title below thumbnail — no truncation */}
                    <span className="text-xs font-semibold text-foreground text-center w-full group-hover:text-primary transition-colors">
                      {nextChart.title}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ChartCarousel;
